/**
 * 駅順マスタ (RouteStopList API 由来) の型定義。
 *
 * - 駅順データは ControlPanel API (Cognito 認証) の `GET /route-stops` プロキシ経由で
 *   取得する。取得処理は `BroadcastPointsClient.getRouteStops` を参照。
 * - レスポンスは GTFS 静的 stops 相当の JSON で、`stops[]` を保持する。
 */

/** 1 駅分の情報 (GTFS 静的 stops 相当) */
export interface RouteStop {
  stop_id: string;
  stop_name: string;
  stop_lat?: number;
  stop_lon?: number;
  stop_sequence: number;
}

/** route_id ごとの駅順マスタ */
export interface RouteStopListFile {
  route_id: string;
  direction_id?: number;
  stops: RouteStop[];
}

/**
 * 駅一覧を stop_sequence 昇順に整列して返すヘルパー。
 * - 配列でない / stop_id を持たない要素は除外する。
 */
export function sortStopsBySequence(stops: RouteStop[] | undefined | null): RouteStop[] {
  if (!Array.isArray(stops)) return [];
  return stops
    .filter((s): s is RouteStop => Boolean(s) && typeof s.stop_id === 'string')
    .slice()
    .sort((a, b) => (a.stop_sequence ?? 0) - (b.stop_sequence ?? 0));
}

/** 駅間判定の結果 (開始駅 → 終了駅) */
export interface StationSegment {
  startStop: RouteStop;
  endStop: RouteStop;
}

/**
 * 緯度経度を持つ駅のみを stop_sequence 昇順で抽出する内部ヘルパー。
 */
function stopsWithLatLon(stops: RouteStop[] | undefined | null): RouteStop[] {
  return sortStopsBySequence(stops).filter(
    (s) => typeof s.stop_lat === 'number' && typeof s.stop_lon === 'number',
  );
}

/**
 * 緯度経度をローカル平面に近似した点-線分の最短距離 (二乗値) を返す。
 *
 * - 対象エリアが狭い (都市圏の鉄道路線) ため、緯度経度を平面座標とみなし、
 *   経度方向は cos(緯度) で補正してメートル相当の比率に近づける。
 * - 返り値は比較用の相対距離であり、実距離 (メートル) ではない点に注意。
 */
function pointToSegmentDistanceSq(
  lat: number,
  lon: number,
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  // 経度補正係数 (この区間付近の緯度を基準にする)
  const cosLat = Math.cos((((aLat + bLat) / 2) * Math.PI) / 180);
  const px = (lon - aLon) * cosLat;
  const py = lat - aLat;
  const vx = (bLon - aLon) * cosLat;
  const vy = bLat - aLat;
  const segLenSq = vx * vx + vy * vy;
  if (segLenSq === 0) {
    // 始点と終点が同一座標の場合は始点との距離
    return px * px + py * py;
  }
  // 線分上への射影パラメータ t を [0, 1] にクランプ
  let t = (px * vx + py * vy) / segLenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const dx = px - t * vx;
  const dy = py - t * vy;
  return dx * dx + dy * dy;
}

/**
 * 指定座標 (lat, lon) が駅順リスト上のどの隣接駅間 (区間) に最も近いかを判定する。
 *
 * - 各隣接駅ペアを線分とみなし、点-線分の最短距離が最小となる区間を採用する。
 * - 緯度経度を持つ駅が 2 駅未満の場合は判定不能として null を返す。
 *
 * @returns 最も近い区間の開始駅 / 終了駅。判定不能なら null。
 */
export function findStationSegment(
  stops: RouteStop[] | undefined | null,
  lat: number,
  lon: number,
): StationSegment | null {
  const ordered = stopsWithLatLon(stops);
  if (ordered.length < 2) return null;

  let best: StationSegment | null = null;
  let bestDistSq = Number.POSITIVE_INFINITY;
  for (let i = 0; i < ordered.length - 1; i++) {
    const a = ordered[i] as RouteStop;
    const b = ordered[i + 1] as RouteStop;
    const distSq = pointToSegmentDistanceSq(
      lat,
      lon,
      a.stop_lat as number,
      a.stop_lon as number,
      b.stop_lat as number,
      b.stop_lon as number,
    );
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = { startStop: a, endStop: b };
    }
  }
  return best;
}

/**
 * 駅間判定結果から「開始駅-終了駅」形式のラベル文字列を生成する。
 * 判定不能 (null) の場合は空文字を返す。
 */
export function formatStationSegmentLabel(segment: StationSegment | null): string {
  if (!segment) return '';
  return `${segment.startStop.stop_name}-${segment.endStop.stop_name}`;
}

/**
 * 緯度経度を持つ駅のみを stop_sequence 昇順で返す (有限値のみ)。
 * 地図右上の「駅へ移動」ドロップダウンの候補に使う。
 */
export function filterStationsWithLatLon(stations: RouteStop[] | undefined | null): RouteStop[] {
  return sortStopsBySequence(stations).filter(
    (s) =>
      typeof s.stop_lat === 'number' &&
      typeof s.stop_lon === 'number' &&
      Number.isFinite(s.stop_lat) &&
      Number.isFinite(s.stop_lon),
  );
}

/**
 * 「駅へ移動」で選択した駅と前後の駅 (存在する方のみ) の座標 `[lat, lng][]` を返す。
 * 地図はこの座標がすべて収まる縮尺でジャンプする。
 * - 選択駅が見つからない場合は空配列。
 * - 先頭 / 末尾の駅は片側の隣駅のみを含む。
 * - `stations` は `filterStationsWithLatLon` 済み (駅順・座標あり) を前提とする。
 */
export function computeStationJumpLatLngs(
  stations: RouteStop[],
  stopId: string,
): [number, number][] {
  const idx = stations.findIndex((s) => s.stop_id === stopId);
  if (idx < 0) return [];
  return stations
    .slice(Math.max(0, idx - 1), idx + 2)
    .map((s) => [s.stop_lat as number, s.stop_lon as number]);
}
